import { FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import ShippingIcon from 'dibs-icons/exports/Shipping';
import { PdpSharedBlurb } from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb';
import PdpSharedShippingInfoWrapper from '../PdpSharedShippingInfo/PdpSharedShippingInfoWrapper';
import { PdpSharedShippingHolidayDelivery } from '../PdpSharedShippingHolidayDelivery/PdpSharedShippingHolidayDelivery';
import { deliveryTitle } from '../messages/pdpDetailsMessages';

import { PdpRespSidebarBlurbShipping_viewer$key } from './__generated__/PdpRespSidebarBlurbShipping_viewer.graphql';
import { PdpRespSidebarBlurbShipping_item$key } from './__generated__/PdpRespSidebarBlurbShipping_item.graphql';
import { PdpRespSidebarBlurbShipping_user$key } from './__generated__/PdpRespSidebarBlurbShipping_user.graphql';
import { PdpRespSidebarBlurbShipping_sku$key } from './__generated__/PdpRespSidebarBlurbShipping_sku.graphql';

// styles
import styles from '../PdpSharedBlurbs/PdpSharedBlurb/PdpSharedBlurb.scss';

const viewerFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_viewer on Viewer {
        pdpHolidayShippingFeatureFlag: isEnabledByLocale(featureName: "HOLIDAY_SHIPPING")
        ...PdpSharedShippingHolidayDelivery_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_user on User {
        ...PdpSharedShippingInfoWrapper_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespSidebarBlurbShipping_item on Item {
        ...PdpSharedShippingInfoWrapper_item
        ...PdpSharedShippingHolidayDelivery_item
        attributes {
            leadTime {
                option {
                    urlLabel
                }
            }
        }
    }
`;

const skuFramgent = graphql`
    fragment PdpRespSidebarBlurbShipping_sku on ItemSkuType {
        ...PdpSharedShippingHolidayDelivery_sku
    }
`;

export const PdpRespSidebarBlurbShipping: FC<{
    item: PdpRespSidebarBlurbShipping_item$key;
    viewer: PdpRespSidebarBlurbShipping_viewer$key;
    user: PdpRespSidebarBlurbShipping_user$key | null;
    sku: PdpRespSidebarBlurbShipping_sku$key | null;
    currency: string;
    renderTooltip?: boolean;
    isClient: boolean;
    itemQuantity?: number | null;
}> = ({
    item: itemRef,
    viewer: viewerRef,
    user: userRef,
    sku: skuRef,
    currency,
    renderTooltip,
    isClient,
    itemQuantity,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const sku = useFragment(skuFramgent, skuRef);

    const { pdpHolidayShippingFeatureFlag } = viewer;
    const leadTime = item.attributes?.leadTime?.[0]?.option?.urlLabel;

    return (
        <>
            <PdpSharedBlurb
                IconComponent={ShippingIcon}
                hideIcon
                zeroPadding
                iconClass={styles.printHide}
            >
                <PdpSharedShippingInfoWrapper
                    item={item}
                    currency={currency}
                    leadTime={leadTime}
                    renderTooltip={renderTooltip}
                    user={user}
                    isClient={isClient}
                    title={<span className={styles.blurbTitle}>{deliveryTitle}</span>}
                    displayInline
                    itemQuantity={itemQuantity}
                    positioning="sidebar"
                />
            </PdpSharedBlurb>
            {pdpHolidayShippingFeatureFlag && (
                <PdpSharedShippingHolidayDelivery
                    useIcon
                    sku={sku}
                    item={item}
                    viewer={viewer}
                    useTooltipDetails
                    isClient={isClient}
                />
            )}
        </>
    );
};
